import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import storage from '@dashboard/services/storage';
import request from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import { FormElement } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Form from '@dashboard/components/Formik/Form';
import {
  AuthDivider,
  AuthFooter,
  AuthHeader,
  AuthTerms,
  authButtonClassName,
  authInputClassName,
  authLink,
  newPasswordField,
  newPasswordSchema,
  renderAuthField,
} from '@dashboard/components/AuthForm';
import Meta from '@dashboard/components/Meta';
import { GoogleSignIn } from '@dashboard/components/GoogleSignIn';

import { signup } from './Signup/helpers';
import { toast } from '@/dashboard/context/ToastContext';
import logException from '../services/logException';
import Link from '../components/Link';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';

const { REACT_APP_API_URL: apiUrl } = process.env;

export default function AcceptInvite() {
  const {
    user,
    setLogged,
    isAuth,
    isAuthenticating,
    login: authLogin,
    logout,
    verifyAuth,
  } = useAuth();
  const history = useHistory();
  const { invite, team } = useUrlQuery();
  const teamTitle = team
    ? `Join the ${team} team on Checkout Page`
    : 'Join your team on Checkout Page';
  const { acceptInvite } = useUserQueries();
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // null | 'accept' | 'login' | 'register' | 'wrong-account' | 'invite-error'
  const [inviteEmail, setInviteEmail] = useState('');

  const acceptInviteMutation = useMutation({
    mutationFn: () => {
      acceptInvite({
        invite,
        session: storage.local.getItem('CheckoutPage.sessionId'),
      });
    },
    onSuccess: async () => {
      await verifyAuth();
      toast('Invite accepted! Welcome to the team.', 'success');
      history.push('/dashboard');
    },
    onError: error => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (isAuthenticating) return;

    const fetchInviteInfo = async () => {
      if (!invite) {
        setMode('register');
        return;
      }

      try {
        const response = await request(`${apiUrl}/get-invite-info`, null, {
          body: JSON.stringify({ invite }),
          method: 'POST',
        });

        if (response.status === 'error') {
          setError(response.message);
          setMode('invite-error');
          return;
        }

        setInviteEmail(response.email);

        if (isAuth && user) {
          if (user.email.toLowerCase() === response.email?.toLowerCase()) {
            setMode('accept');
          } else {
            setMode('wrong-account');
          }
          return;
        }

        if (response.hasAccount) {
          setMode('login');
        } else {
          setMode('register');
        }
      } catch (error) {
        if (error.message === 'You must sign up with the email address that was invited.') {
          setMode('wrong-account');
        } else {
          logException({ message: 'Error fetching invite info', error });
          setError(error.message);
          setMode('invite-error');
        }
      }
    };

    fetchInviteInfo();
  }, [isAuth, isAuthenticating, invite, user]);

  const handleGoogleSuccess = result => {
    if (result.inviteAccepted) {
      toast('Invite accepted! Welcome to the team.', 'success');
      history.push('/dashboard');
    } else {
      toast('Invite accepted! Please complete your profile.', 'success');
      history.push('/dashboard');
    }
  };

  const handleGoogleError = error => {
    setError(error.message);
  };

  if (isAuthenticating || mode === null) {
    return (
      <AuthLayout>
        <div className="text-center">
          <p>Loading...</p>
        </div>
      </AuthLayout>
    );
  }

  if (mode === 'invite-error') {
    return (
      <>
        <Meta
          meta={{
            title: 'Invalid invite',
          }}
        />

        <AuthLayout>
          <AuthHeader title="Invalid invite" />

          <div>
            <Alert variant="destructive" className="my-4">
              <ExclamationCircleIcon />
              <AlertTitle>Invalid invite</AlertTitle>
              <AlertDescription>
                <p>{error || 'This invite link is invalid or has expired.'}</p>
              </AlertDescription>
            </Alert>

            <Button
              onClick={() => history.push('/login')}
              size="full"
              appearance="primary"
              className={authButtonClassName}
            >
              Go to login
            </Button>
          </div>
        </AuthLayout>
      </>
    );
  }

  if (mode === 'wrong-account') {
    return (
      <>
        <Meta
          meta={{
            title: 'Account mismatch',
          }}
        />

        <AuthLayout>
          <AuthHeader title="Account mismatch" />

          <div>
            <p className="mb-4 text-[15px] leading-relaxed text-gray-700 dark:text-muted-foreground">
              To accept this invite, please log out and sign in with the email address the invite
              was sent to.
            </p>

            <Button
              onClick={() => {
                logout({ withoutRedirect: true });
                window.location.href = `/accept-invite?invite=${invite}&team=${team}`;
              }}
              size="full"
              appearance="primary"
              className={authButtonClassName}
            >
              Log out and continue
            </Button>
          </div>

          <p className="text-center text-sm text-gray-600 dark:text-muted-foreground">
            Or{' '}
            <Link to="/dashboard" bare className={authLink}>
              go to dashboard
            </Link>
          </p>
        </AuthLayout>
      </>
    );
  }

  if (mode === 'accept' && isAuth) {
    return (
      <>
        <Meta
          meta={{
            title: 'Accept invite',
          }}
        />

        <AuthLayout>
          <AuthHeader title={teamTitle} />

          <div>
            <p className="mb-4 text-[15px] leading-relaxed text-gray-700 dark:text-muted-foreground">
              You've been invited to collaborate on the {team} store. Accept to get started.
            </p>

            <FormElement>
              <FormErrorBase>{error}</FormErrorBase>
            </FormElement>

            <Button
              onClick={() => acceptInviteMutation.mutate()}
              size="full"
              appearance="primary"
              className={authButtonClassName}
              disabled={acceptInviteMutation.isPending}
            >
              {acceptInviteMutation.isPending ? 'Accepting...' : 'Accept invite'}
            </Button>
          </div>

          <p className="text-center text-sm text-gray-600 dark:text-muted-foreground">
            You'll be able to switch between your stores from the dashboard.
          </p>
        </AuthLayout>
      </>
    );
  }

  if (mode === 'login') {
    return (
      <>
        <Meta
          meta={{
            title: 'Log in and accept invite',
          }}
        />

        <AuthLayout>
          <AuthHeader title={teamTitle} description="Log in to accept your invite." />

          <div>
            {/* Google Sign-In */}
            <div>
              <GoogleSignIn
                mode="invite"
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />
            </div>

            <AuthDivider />

            {/* Login Form */}
            <Form
              key="login"
              initialValues={{
                email: inviteEmail,
                password: '',
              }}
              enableReinitialize
              onSubmit={async values => {
                const response = await authLogin({
                  ...values,
                  invite,
                  session: storage.local.getItem('CheckoutPage.sessionId'),
                });

                return {
                  data: response.data,
                  error: response.error || response.message,
                  onSuccess: () => {
                    setLogged({
                      user: response.data.user,
                      seller: response.data.user.currentSeller,
                      token: response.data.token,
                    });

                    toast('Logged in and invite accepted!', 'success');
                    history.push('/dashboard');
                  },
                };
              }}
              showState={false}
              inputClassName={authInputClassName}
              renderField={renderAuthField}
              fields={[
                {
                  name: 'email',
                  label: 'Email',
                  placeholder: 'name@example.com',
                  type: 'email',
                  required: true,
                  autoComplete: 'username',
                },
                {
                  name: 'password',
                  label: 'Password',
                  type: 'password',
                  required: true,
                  autoComplete: 'current-password',
                },
              ]}
              submitButton={{
                label: 'Log in and accept invite',
                appearance: 'primary',
                size: 'full',
                className: authButtonClassName,
              }}
            />
          </div>

          <AuthFooter>
            New to Checkout Page?{' '}
            <Link bare className={authLink} onClick={() => setMode('register')}>
              Create a free account
            </Link>
          </AuthFooter>
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Meta
        meta={{
          title: 'Create account and accept invite',
        }}
      />

      <AuthLayout legalLinks={false}>
        <AuthHeader title={teamTitle} description="Create your account to get started." />

        <div>
          {/* Google Sign-In */}
          <div>
            <GoogleSignIn
              mode="invite"
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
          </div>

          <AuthDivider />

          {/* Register Form */}
          <Form
            key="register"
            initialValues={{
              name: '',
              email: inviteEmail,
              password: '',
              marketingConsent: true,
            }}
            enableReinitialize
            validationSchema={newPasswordSchema}
            validateOnBlur
            validateOnChange
            onSubmit={async (values, actions) => {
              const response = await signup({
                ...values,
                session: storage.local.getItem('CheckoutPage.sessionId'),
                invite,
              });

              if (!response.data) {
                return {
                  data: null,
                  error: response.error || response.message,
                };
              }

              actions.resetForm();

              return {
                data: {
                  name: response.data.user.name,
                  email: response.data.user.email,
                },
                onSuccess: () => {
                  setLogged({
                    user: response.data.user,
                    seller: response.data.user.currentSeller,
                    token: response.data.token,
                  });

                  toast('Account created and invite accepted!', 'success');
                  history.push('/dashboard');
                },
              };
            }}
            showState={false}
            inputClassName={authInputClassName}
            renderField={renderAuthField}
            fields={[
              {
                name: 'name',
                label: 'First name',
                autoComplete: 'given-name',
                hidePlaceHolder: true,
                type: 'text',
                required: true,
              },
              {
                name: 'email',
                label: 'Email',
                placeholder: 'name@example.com',
                type: 'email',
                required: true,
                autoComplete: 'username',
              },
              newPasswordField,
            ]}
            submitButton={{
              label: 'Create account and accept invite',
              appearance: 'primary',
              size: 'full',
              className: authButtonClassName,
            }}
          />
          <AuthTerms />
        </div>

        <AuthFooter>
          Already have an account?{' '}
          <Link bare className={authLink} onClick={() => setMode('login')}>
            Log in
          </Link>
        </AuthFooter>
      </AuthLayout>
    </>
  );
}
